<script lang="ts" setup>
import CopilotActivationService from '@/core/jobs/copilot-activation/copilot-activation.service';
import TextCheckOptions from '@/components/Jobs/CopilotActivation/TextCheckOptions.vue';
import { computed, ref } from 'vue';
import { PolicyEnforcement } from '@factoryfixinc/ats-interfaces';
import PolicyTooltip from './PolicyTooltip.vue';

const copilotActivationService = new CopilotActivationService();

const props = defineProps<{
  isDisabled: boolean;
  copilotOutreachPolicy?: PolicyEnforcement;
}>();

const isDisableActiveOutreachDialogOpen = ref(false);

const isActiveOutreachEnabled = computed<boolean>(
  () => copilotActivationService.activeOutreachEnabled === true,
);

const isRequiredPolicy = computed(() => props.copilotOutreachPolicy === PolicyEnforcement.REQUIRED);
const isForbiddenPolicy = computed(
  () => props.copilotOutreachPolicy === PolicyEnforcement.FORBIDDEN,
);

const setActiveOutreachEnabled = (selected: boolean, override: boolean = false) => {
  if (selected && isForbiddenPolicy.value) {
    return;
  }

  if (!selected && isRequiredPolicy.value) {
    return;
  }

  if (!override && copilotActivationService.originalActiveOutreachEnabled && !selected) {
    isDisableActiveOutreachDialogOpen.value = true;
  } else {
    copilotActivationService.activeOutreachEnabled = selected;
  }
};

const handleCancel = () => {
  isDisableActiveOutreachDialogOpen.value = false;
};

const handleSwitch = () => {
  isDisableActiveOutreachDialogOpen.value = false;
  setActiveOutreachEnabled(false, true);
};
</script>

<template>
  <div id="copilot-outreach" :class="{ 'pointer-events-none opacity-75': isDisabled }">
    <div class="copilot-outreach-title">Who should your AI recruiter source for this job?</div>
    <div class="flex flex-row gap-3">
      <TextCheckOptions
        :is-selected="isActiveOutreachEnabled && !isForbiddenPolicy"
        :is-disabled="isForbiddenPolicy"
        class="flex-1"
        @click="setActiveOutreachEnabled(true)"
      >
        <template v-slot:title>
          <span class="flex items-center justify-between">
            Active job seekers + passive candidates
            <PolicyTooltip
              v-if="isForbiddenPolicy"
              :feature-policy="copilotOutreachPolicy"
              feature="Active job seekers + passive candidates"
            />
          </span>
        </template>
        Source from active job seekers and qualified passive talent in the FactoryFix network
      </TextCheckOptions>

      <TextCheckOptions
        :is-selected="!isActiveOutreachEnabled && !isRequiredPolicy"
        :is-disabled="isRequiredPolicy"
        class="flex-1"
        @click="setActiveOutreachEnabled(false)"
      >
        <template v-slot:title>
          <span class="flex items-center justify-between">
            Active job seekers only
            <PolicyTooltip
              v-if="isRequiredPolicy"
              :feature-policy="copilotOutreachPolicy"
              feature="Active job seekers only"
            />
          </span>
        </template>
        Source only from candidates actively searching for a new job
      </TextCheckOptions>
    </div>
  </div>

  <v-dialog
    :model-value="isDisableActiveOutreachDialogOpen"
    max-width="512px"
    height="226px"
    persistent
  >
    <div class="rounded-lg bg-white p-6">
      <img
        class="float-right mt-1 cursor-pointer"
        src="@/assets/svg/close-black.svg"
        alt="close"
        width="20"
        height="20"
        @click="handleCancel"
      />
      <p class="mb-2 font-serif text-2xl font-black text-copilot">Are you sure?</p>
      <p class="mb-12 font-sans font-normal text-shade-800">
        Saving this change will
        <span class="font-bold text-shade-900">stop all ongoing candidate outreach.</span> Your AI
        recruiter will no longer contact or respond to passive candidates for this job.
      </p>
      <!-- Actions -->
      <div class="flex items-center justify-end">
        <v-btn
          :ripple="false"
          class="modal-button-text mr-4"
          variant="flat"
          @click.prevent="handleCancel"
          >Cancel</v-btn
        >
        <v-btn
          :ripple="false"
          class="modal-button-primary"
          variant="flat"
          @click.prevent="handleSwitch"
        >
          Switch to active-only sourcing
        </v-btn>
      </div>
    </div>
  </v-dialog>
</template>
<style lang="postcss" scoped>
.copilot-outreach-title {
  @apply mb-4 text-lg font-extrabold;
}
</style>
